import { createSignal } from "solid-js";

/** A plant's recipe, as the server's state lists it: what a line takes and
 *  makes each cycle, the lines a built plant has, and the cycle's length in
 *  ticks (10 a second, before research speed-ups). */
export interface Recipe {
  name: string;
  inputs: [number, string, number][];
  outputs: [number, string, number][];
  lines?: number;
  ticks?: number;
}

/** The plants there are; these names until the first state says. */
const [recipes, setRecipeList] = createSignal<Recipe[]>(
  ["Steelworks", "Munitions plant", "Recruiting office", "Locomotive works", "Copper smelter", "Refinery", "Mint"].map((name) => ({ name, inputs: [], outputs: [] })),
);
export { recipes };

/** Takes the state's list when it differs (ticks and lines included). */
export function setRecipes(list: Recipe[]): void {
  if (JSON.stringify(list) !== JSON.stringify(recipes())) setRecipeList(list);
}

/** Ticks a cycle (the server's; 10 until the first state says). */
export const ticksOf = (r: Recipe): number => r.ticks ?? 10;

/** Lines a built plant of `r` has (the server's, else 8). */
export const linesOf = (r: Recipe): number => r.lines ?? 8;

/** A good's rate a minute at `n` a cycle for a plant of `lines` lines. */
export const perMinute = (r: Recipe, n: number, lines = linesOf(r)): number => (n * lines * 600) / ticksOf(r);

/** The picker's tabs, by chain; a recipe not named here goes in Other. */
export const RECIPE_TABS: { name: string; icon: string; recipes: RegExp }[] = [
  { name: "Steel", icon: "Steel", recipes: /^(Steelworks|Munitions plant|Locomotive works|Armour steel|Carbide works)/ },
  { name: "Copper and tin", icon: "Copper", recipes: /^(Copper smelter|Bronze foundry)/ },
  { name: "Light metals", icon: "Light alloy", recipes: /^(Aluminium works|Light alloy works|Battery works|Magnet works)/ },
  { name: "Fuel and chemicals", icon: "Fuel", recipes: /^(Refinery|Catalytic refinery|Explosives plant)/ },
  { name: "People and money", icon: "Credits", recipes: /^(Recruiting office|Mint)/ },
];

/** The tab a recipe sits in ("Other" when none claims it). */
export const tabOf = (name: string): string => RECIPE_TABS.find((t) => t.recipes.test(name))?.name ?? "Other";

/** A recipe by name. */
export const recipeByName = (name: string): Recipe | undefined => recipes().find((r) => r.name === name);
